import React from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BUILDER_URL } from '../lib/externalLinks';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import './Hero.css';

const primaryActionClasses =
  'inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-csl-green text-white font-semibold rounded-xl hover:bg-csl-dark-green transition-all duration-300 shadow-lg hover:shadow-xl group hover:-translate-y-1 hover:scale-105 active:scale-95 text-sm sm:text-base w-full sm:w-auto text-center';

const secondaryActionClasses =
  'inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-csl-green hover:text-csl-green transition-all duration-300 group hover:scale-105 active:scale-95 text-sm sm:text-base w-full sm:w-auto text-center';

/**
 * Slide call-to-action. Navigation is rendered as a link — not a button — so it
 * keeps middle-click, "open in a new tab" and the right screen-reader role.
 */
const SlideAction = ({ href, isRoute, isExternal, className, children }) => {
  if (isRoute) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={className}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  );
};

const Hero = () => {
  const { t } = useTranslation();

  // WCAG 2.2.2: visitors who asked for less motion drive the carousel
  // themselves instead of having it rotate under them.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const slides = [
    {
      id: 1,
      title: t('hero.slides.1.title'),
      subtitle: t('hero.slides.1.subtitle'),
      description: t('hero.slides.1.description'),
      buttonText: t('hero.slides.1.button'),
      buttonLink: "#services",
      secondaryButtonText: t('builder.cta'),
      secondaryButtonLink: BUILDER_URL,
      secondaryButtonIsExternal: true,
      image: "/CSL-BRANDS-LOGO.png",
      imageAlt: t('hero.slides.1.imageAlt'),
      bgColor: "bg-gradient-to-br from-gray-50 to-white",
      customImageClass: "w-auto mx-auto max-h-[350px] object-contain hover:scale-105 transition-transform duration-300"
    },
    {
      id: 2,
      title: t('hero.slides.2.title'),
      subtitle: t('hero.slides.2.subtitle'),
      description: t('hero.slides.2.description'),
      buttonText: t('hero.slides.2.button'),
      buttonLink: "https://kursa.csl-brands.com/?demo=yes",
      image: "/images/kursa-the-all-one-platform-to-sell-your-online-courses.jpg",
      imageAlt: t('hero.slides.2.imageAlt'),
      bgColor: "bg-gradient-to-br from-gray-50 to-white"
    },
    {
      id: 3,
      title: t('hero.slides.3.title'),
      subtitle: t('hero.slides.3.subtitle'),
      description: t('hero.slides.3.description'),
      buttonText: t('hero.slides.3.button'),
      buttonLink: "https://bootcamps.csl-brands.com",
      image: "/images/csl-brands-team-of-developers.jpeg",
      imageAlt: t('hero.slides.3.imageAlt'),
      bgColor: "bg-gradient-to-br from-blue-50 to-white"
    },
    {
      id: 4,
      title: t('hero.slides.4.title'),
      subtitle: t('hero.slides.4.subtitle'),
      description: t('hero.slides.4.description'),
      buttonText: t('hero.slides.4.button'),
      buttonLink: "https://erp.csl-brands.com",
      image: "CSL-Brands-ERP-CRM Platform.jpg",
      imageAlt: t('hero.slides.4.imageAlt'),
      bgColor: "bg-gradient-to-br from-green-50 to-white"
    },
    {
      id: 5,
      title: t('hero.slides.5.title'),
      subtitle: t('hero.slides.5.subtitle'),
      description: t('hero.slides.5.description'),
      buttonText: t('hero.slides.5.button'),
      buttonLink: "https://tchopnjoh.csl-brands.com",
      image: "/CSL-Brands-Restaurant-SaaS.jpg",
      imageAlt: t('hero.slides.5.imageAlt'),
      bgColor: "bg-gradient-to-br from-orange-50 to-white"
    },
    {
      id: 6,
      title: t('hero.slides.6.title'),
      subtitle: t('hero.slides.6.subtitle'),
      description: t('hero.slides.6.description'),
      buttonText: t('hero.slides.6.button'),
      buttonLink: "/talents",
      isRoute: true,
      image: "/training/csl-brands-formation-en-marketing-digital-e-commerce-7.jpeg",
      imageAlt: t('hero.slides.6.imageAlt'),
      bgColor: "bg-gradient-to-br from-purple-50 to-white"
    }
  ];

  return (
    <section className="relative min-h-[600px] h-[100svh] overflow-hidden">
      <Swiper
        modules={[A11y, Navigation, Pagination, Autoplay, EffectFade]}
        effect="fade"
        spaceBetween={0}
        slidesPerView={1}
        navigation
        pagination={{ clickable: true }}
        autoplay={prefersReducedMotion ? false : { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: true }}
        loop={true}
        className="h-full w-full"
      >
        {slides.map((slide) => {
          // Only the opening slide carries the page heading; the others are
          // panels of the same carousel, not competing page titles.
          const isLeadSlide = slide.id === 1;
          const TitleTag = isLeadSlide ? 'h1' : 'h2';

          return (
            <SwiperSlide key={slide.id}>
              <div className={`relative h-full w-full flex items-center justify-center ${slide.bgColor} overflow-hidden`}>
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 pointer-events-none">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }} />
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-20 pb-16">
                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Content */}
                    <div className="text-center lg:text-left relative z-20 order-2 lg:order-1">
                      <div className="inline-flex items-center px-3 py-1.5 bg-csl-green/10 rounded-full text-csl-green font-semibold text-xs sm:text-sm mb-4 sm:mb-6 animate-slide-up">
                        {slide.subtitle}
                      </div>

                      <TitleTag className="text-3xl sm:text-4xl lg:text-5xl xl:text-7xl font-bold text-gray-900 leading-tight mb-4 sm:mb-6 animate-slide-up [animation-delay:200ms]">
                        {slide.title}
                      </TitleTag>

                      <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed animate-slide-up [animation-delay:400ms]">
                        {slide.description}
                      </p>

                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start animate-slide-up [animation-delay:600ms]">
                        <SlideAction
                          href={slide.buttonLink}
                          isRoute={slide.isRoute}
                          className={primaryActionClasses}
                        >
                          <span className="text-center">{slide.buttonText}</span>
                          <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                        </SlideAction>

                        {slide.secondaryButtonText && (
                          <SlideAction
                            href={slide.secondaryButtonLink}
                            isExternal={slide.secondaryButtonIsExternal}
                            className={secondaryActionClasses}
                          >
                            <span className="text-center">{slide.secondaryButtonText}</span>
                          </SlideAction>
                        )}
                      </div>
                    </div>

                    {/* Image */}
                    <div className="relative z-10 animate-slide-right [animation-delay:200ms] order-1 lg:order-2">
                      <div className="relative">
                        <img
                          src={slide.image}
                          alt={slide.imageAlt}
                          loading={isLeadSlide ? 'eager' : 'lazy'}
                          fetchPriority={isLeadSlide ? 'high' : 'auto'}
                          decoding="async"
                          className={slide.customImageClass || "w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-300 object-cover max-h-[300px] sm:max-h-[400px] lg:max-h-[500px]"}
                        />

                        {/* Floating Cards - Only for specific slides if needed, or generic */}
                        {slide.id === 2 && (
                          <>
                            <div className="absolute -top-6 -left-6 bg-white p-4 rounded-xl shadow-lg border border-gray-100 animate-scale-in [animation-delay:800ms] hidden md:block">
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-csl-green rounded-full animate-pulse"></div>
                                <span className="text-sm font-semibold text-gray-700">{t('hero.cards.liveCourses')}</span>
                              </div>
                            </div>
                            <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg border border-gray-100 animate-scale-in [animation-delay:1200ms] hidden md:block">
                              <div className="text-center">
                                <div className="text-lg font-bold text-csl-green">∞</div>
                                <div className="text-xs text-gray-600">{t('hero.cards.unlimitedAccess')}</div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
};

export default Hero;
